import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, List, X } from 'lucide-react';
import { PresentationLogo } from './PresentationLogo';
import { slides, slideTitles } from './slides';
import { useScreenInit } from '../useScreenInit.js';
export function Deck() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.slide === 'number' ? screenInit.slide : 0);
  const [direction, setDirection] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentIndex < slides.length - 1) {
          setDirection(1);
          setCurrentIndex(prev => prev + 1);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentIndex > 0) {
          setDirection(-1);
          setCurrentIndex(prev => prev - 1);
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        setDirection(-1);
        setCurrentIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setDirection(1);
        setCurrentIndex(slides.length - 1);
      } else if (e.key === 'Escape') {
        setTocOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);
  const goTo = (idx: number) => {
    setDirection(idx > currentIndex ? 1 : -1);
    setCurrentIndex(idx);
  };
  const goPrev = () => currentIndex > 0 && goTo(currentIndex - 1);
  const goNext = () => currentIndex < slides.length - 1 && goTo(currentIndex + 1);
  const CurrentSlide = slides[currentIndex];
  return <div className="deck-root relative w-screen h-screen overflow-hidden bg-[var(--bg)]" data-id="element-1" data-mp="1f8d7fkj65j3b">
      <AnimatePresence mode="wait" custom={direction} data-id="element-2" data-mp="1f6imi67wdqz8">
        <motion.div key={currentIndex} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 40 : -40
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -40 : 40
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} className="absolute inset-0" data-id="element-3" data-mp="39hiawbycnynt">
          <CurrentSlide index={currentIndex} total={slides.length} data-id="element-4" data-mp="2vpk8ejz5910m" />
        </motion.div>
      </AnimatePresence>

      <PresentationLogo data-id="element-5" data-mp="2gtcq5p7aq9nz" />

      {/* Top progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-black/5 z-40 hide-on-print" data-id="element-6" data-mp="2lq37sgyr1dbi">
        <motion.div className="h-full bg-[var(--brand)]" animate={{
        width: `${(currentIndex + 1) / slides.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-7" data-mp="f0eullm4x2zc" />
      </div>

      {/* Top left control */}
      <button onClick={() => setTocOpen(true)} aria-label="Open table of contents" className="absolute left-3 top-3 z-50 flex h-8 items-center gap-1.5 rounded-full border border-black/10 bg-white/90 px-3.5 text-[var(--brand)] shadow-sm backdrop-blur transition-colors hover:bg-[var(--surface)] hide-on-print" data-id="element-8" data-mp="1zj8uirljnbdh">
        <List className="h-3.5 w-3.5" data-id="element-9" data-mp="2n9v10efwgnld" />
        <span className="text-xs font-semibold" data-id="element-10" data-mp="2j6q18jn7f00f">Contents</span>
      </button>

      {/* Bottom nav cluster */}
      <div className="absolute bottom-3 right-3 z-50 flex items-center gap-2 hide-on-print" data-id="element-11" data-mp="2x67r72c8kjbd">
        <button onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-12" data-mp="2s6li953fxp8d">
          <ChevronLeft className="h-4 w-4" data-id="element-13" data-mp="1493w28zyuvkv" />
        </button>
        <div className="flex items-baseline gap-1 rounded-full bg-[var(--brand)] px-3.5 py-1.5 shadow-md" data-id="element-14" data-mp="147j0cg1x16lf">
          <span className="text-white font-bold text-sm tabular-nums" data-id="element-15" data-mp="198ymkbftxj5e">
            {currentIndex + 1}
          </span>
          <span className="text-[var(--brand-lighter)] text-xs font-medium" data-id="element-16" data-mp="1e4xhyizks1fb">
            / {slides.length}
          </span>
        </div>
        <button onClick={goNext} disabled={currentIndex === slides.length - 1} aria-label="Next slide" className="flex h-8 w-8 items-center justify-center rounded-full border border-black/10 bg-white/90 text-[var(--brand)] shadow-sm backdrop-blur transition-all hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-17" data-mp="li2vxhn40tzq">
          <ChevronRight className="h-4 w-4" data-id="element-18" data-mp="2s4rdbiitj0u" />
        </button>
      </div>

      {/* Collapsible TOC */}
      <AnimatePresence data-id="element-19" data-mp="2g24kav4xrsv6">
        {tocOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} onClick={() => setTocOpen(false)} className="absolute inset-0 z-[60] bg-black/40 backdrop-blur-sm" data-id="element-20" data-mp="2g1lay2tcqcsr" />
            <motion.nav initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          duration: 0.35,
          ease: [0.4, 0, 0.2, 1]
        }} aria-label="Table of contents" className="absolute top-0 left-0 bottom-0 z-[70] w-[min(22rem,85vw)] bg-white shadow-2xl flex flex-col" data-id="element-21" data-mp="1sca7gdkma4ax">
              <div className="flex items-center justify-between px-6 py-5 border-b border-black/5" data-id="element-22" data-mp="zpaldkw38yp6">
                <div data-id="element-23" data-mp="14q7yvk65l4kc">
                  <p className="text-[var(--brand)] text-xs font-semibold uppercase tracking-wider" data-id="element-24" data-mp="14qq5naqfz16i">
                    Snakker · Interactive content
                  </p>
                  <h2 className="text-[var(--fg)] font-bold text-lg" data-id="element-25" data-mp="usaex8vbttxj">
                    Contents
                  </h2>
                </div>
                <button onClick={() => setTocOpen(false)} aria-label="Close table of contents" className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-[var(--surface)] text-[var(--muted)]" data-id="element-26" data-mp="1ij603n5a1qvt">
                  <X className="h-5 w-5" data-id="element-27" data-mp="1doa82n01j3ip" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-2" data-id="element-28" data-mp="21e4ikxww0fzs">
                {slideTitles.map((s, idx) => {
              const active = idx === currentIndex;
              return <button key={idx} onClick={() => {
                goTo(idx);
                setTocOpen(false);
              }} className={`w-full flex items-center gap-4 px-6 py-3 text-left transition-colors ${active ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]/60'}`} data-id="element-29" data-mp="3qvi608qjg88e" data-mpi={idx}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-[var(--brand)] text-white' : 'bg-[var(--surface)] text-[var(--muted)]'}`} data-id="element-30" data-mp="37yqfpfckwsuu">
                        {idx + 1}
                      </span>
                      <span className="min-w-0" data-id="element-31" data-mp="3cvqmjsh95zn0">
                        <span className={`block text-[0.65rem] font-semibold uppercase tracking-wider ${active ? 'text-[var(--brand-light)]' : 'text-[var(--muted)]'}`} data-id="element-32" data-mp="x5e3ippeev91">
                          {s.section}
                        </span>
                        <span className={`block text-sm font-semibold truncate ${active ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-33" data-mp="2qod0pd0aelnm">
                          {s.title}
                        </span>
                      </span>
                    </button>;
            })}
              </div>
            </motion.nav>
          </>}
      </AnimatePresence>
    </div>;
}